import { Container, Texture } from 'pixi.js';
import { gsap } from 'gsap';
import { pixiComponents } from './index';
import { BetBlock } from './betBlock';
import {
  ChartBlockConfigs,
  chartBlockStyles,
  fastTradeAnimationNames,
  FastTradeSoundsName,
  gameImages,
  marketTypes,
} from '../../fastTradeCore/constants';
import { isMobile } from '../../../../core/helpers/isMobile';
import { userInfoStore } from '../../../../store/userInfoStore';
import { mathConfig, mathJS } from '../../../../core/helpers/mathFormat';
import { notificationStore } from '../../../../store/notificationStore';
import {
  ResponseTypeForGamesByRound,
  ValidationResponseCodeForGameByRound,
} from '../../../../core/constants/validationType';
import { fastTradeBetStore } from '../../fastTradeStore/betStore';
import { soundService } from '../../../../services/baseSoundService/soundService';
import { applicationStore } from '../../../../store/applicationStore';

const {
  defaultBlock,
  selectedBlock,
  disabledBlock,
  defaultArrow,
  winBlock,
  selectedArrow,
  Bitcoin,
  actionSelected,
  underDefault,
  overDefault,
  actionWin,
  overDisable,
  actionDisable,
  underDisable,
} = gameImages;

const {
  mobFirstBetBlockX,
  webFirstBetBlockX,
  mobFirstBetBlockY,
  webFirstBetBlockY,
  mobWidth,
  webWidth,
  mobHeight,
  webHeight,
  winBlockColor,
  selectedBlockColor,
} = ChartBlockConfigs;

const { oddTextStyleWeb, oddTextStyleMob, actionNameX, actionNameY, actionArrowX, actionArrowY } =
  chartBlockStyles;

export class BetBlocks {
  betModel = [];

  betTypes = marketTypes;

  roundInfo = {};

  historyBlocks = {};

  betBlocks = [];

  betBlocksArray = [];

  actionBlocksArray = [];

  historyBlocksArray = [];

  firstBetBlockX = isMobile() ? mobFirstBetBlockX : webFirstBetBlockX;

  firstBetBlockY = isMobile() ? mobFirstBetBlockY : webFirstBetBlockY;

  blockWidth = (isMobile() ? mobWidth : webWidth) + (isMobile() ? 1 : 1.4);

  blockHeight = (isMobile() ? mobHeight : webHeight) + (isMobile() ? 0.9 : 1.3);

  isShow = false;

  selectedAmount = 0;

  constructor(parent) {
    this.parent = parent;
    this.initHistory();
    this.init();
  }

  init() {
    this.container = new Container();
    this.parent.addChild(this.container);
  }

  initHistory() {
    this.historyContainer = new Container();
    this.parent.addChild(this.historyContainer);
  }

  setRoundInfo(data) {
    this.roundInfo = data;
  }

  handleWiningBets(bets) {
    this.handleHistoryBlocksCreator();
    this.handleWiningActions(bets);
  }

  handleRemoveActionsData() {
    this.actionBlocksArray.forEach((item) => {
      // item.amountText.text = '';
      item.betBlockItem.isSelected = false;
      item.amountText.text = +mathJS.format(
        this.selectedAmount * item.betBlockItem.odd,
        mathConfig
      );
      item.bgSprite.texture = Texture.from(
        item.betBlockItem.marketType === this.betTypes.over ? overDefault : underDefault
      );
      item.bgSprite.cursor = 'pointer';
      this.actionPointerDownHandler(item, item.betBlockItem.marketType);
    });
  }

  handleWiningActions(bets) {
    this.actionBlocksArray.forEach((item) => {
      const currentActionBet = bets.markets.find(
        (action) => +action.marketType === +item.betBlockItem.marketType
      );
      if (currentActionBet && currentActionBet.winAmount) {
        item.bgSprite.texture = Texture.from(actionWin);
        setTimeout(() => {
          item.winSpine.visible = true;
          item.winSpine.play(fastTradeAnimationNames.winAction);
          item.winSpine.addListener(
            'complete',
            () => {
              item.winSpine.visible = false;
            },
            item.winSpine,
            fastTradeAnimationNames.winAction
          );
        }, 50);
      } else {
        item.bgSprite.texture = Texture.from(
          item.betBlockItem.marketType === this.betTypes.over ? overDefault : underDefault
        );
      }
    });
  }

  handleDisableActions() {
    this.actionBlocksArray.forEach((item) => {
      const isActionInBetModel = this.betModel.find(
        (cur) => item.betBlockItem.marketType === cur.marketType
      );
      if (isActionInBetModel) {
        item.bgSprite.texture = Texture.from(actionDisable);
      } else {
        item.bgSprite.texture = Texture.from(
          item.betBlockItem.marketType === this.betTypes.over ? overDisable : underDisable
        );
        item.bgSprite.cursor = 'default';
      }
      item.container.removeAllListeners('pointerdown');
    });
  }

  moveHistoryContainer(step) {
    this.historyBlocksArray.forEach((item) => {
      gsap.to(item.container.position, {
        x: item.container.x - step,
        ease: 'none',
        duration: 0.7,
      });
    });
  }

  moveHistoryContainerVertical(curStep, lastStep) {
    this.historyBlocksArray = this.historyBlocksArray.map((item) => {
      const curStepValue = curStep / pixiComponents.PixiComponent.chart.dotGrowth;
      const lastStepValue = lastStep / pixiComponents.PixiComponent.chart.dotGrowth;
      const middleStep =
        Math.abs(curStepValue - (curStepValue % 5) - (lastStepValue - (lastStepValue % 5))) / 5;
      const yPosition = Math.abs(middleStep * this.blockHeight);
      const currentDeviation =
        ((lastStep / pixiComponents.PixiComponent.chart.dotGrowth) % 5) *
        pixiComponents.PixiComponent.chart.dotGrowth;
      const y =
        (lastStep > curStep ? item.betBlockItem.y + yPosition : item.betBlockItem.y - yPosition) +
        currentDeviation -
        pixiComponents.PixiComponent.chart.pixelDeviation;
      gsap.to(item.container.position, {
        y,
        ease: 'none',
        duration: 0.7,
      });
      return { ...item, betBlockItem: { ...item.betBlockItem, y } };
    });
  }

  setBetBlocks(data) {
    this.removeBetBlocks();
    const dataKeys = Object.keys(data);
    this.betBlocks = { ...data, row: data[dataKeys[dataKeys.length - 1]] };
    this.setEmptyHistoryBlocks(data);
    this.container.removeChildren();
    this.handleBetBlocksCreator();
  }

  setEmptyHistoryBlocks(data) {
    this.historyBlocks = {
      ...this.historyBlocks,
      ...Object.keys(data).reduce((acc, cur) => {
        if (!this.historyBlocks.hasOwnProperty(cur)) {
          acc[cur] = [];
        }
        return acc;
      }, {}),
    };
  }

  removeBetBlocks() {
    this.betBlocks = [];
    this.betBlocksArray = [];
  }

  removeBetModel() {
    this.betModel = [];
  }

  setHistoryBlocks() {
    this.historyBlocks = this.betModel.reduce((acc, cur) => {
      if (acc[cur.roundId]) {
        acc[cur.roundId] = [...acc[cur.roundId], cur];
      }
      return acc;
    }, this.historyBlocks);
    this.handleHistoryBlocksCreator();
  }

  handleRemoveHistoryBlocks() {
    this.historyBlocksArray = [];
    this.historyBlocks = [];
    this.historyContainer.removeChildren();
  }

  handleAddHistoryBlocksInitial(data, betBlocks) {
    const lastColumnFirstElemIndex = 14;
    const lastColumnFirstElemX = this.betBlocksArray[lastColumnFirstElemIndex].container.x;
    const lastColumnFirstElemY = this.betBlocksArray[lastColumnFirstElemIndex].container.y;
    const lastColumnFirstElemMin = this.betBlocksArray[lastColumnFirstElemIndex].betBlockItem.min;
    const initialHistory = data;
    Object.keys(initialHistory)
      .reverse()
      .forEach((key, keyIndex) => {
        initialHistory[key] = initialHistory[key].map((item) => {
          const stepCount = (lastColumnFirstElemMin - item.point) / 5;
          const x = lastColumnFirstElemX - keyIndex * this.blockWidth;
          const y = lastColumnFirstElemY + stepCount * this.blockHeight;
          return {
            ...item,
            min: item.point,
            max: item.point + 5,
            roundId: key,
            x,
            y,
          };
        });
      });
    this.historyBlocks = initialHistory;
    this.handleHistoryBlocksCreator(true);
    this.setBetBlocks(betBlocks);
  }

  setIsShow(bool) {
    this.isShow = bool;
    this.container.visible = bool;
  }

  setActionOddStyles() {
    this.actionBlocksArray.forEach((item) => {
      const { gameCanvasTranslations } = applicationStore;
      item.actionName.text =
        item.betBlockItem.marketType === marketTypes.over
          ? gameCanvasTranslations.over
          : gameCanvasTranslations.under;
      item.oddText.style = isMobile() ? oddTextStyleMob : oddTextStyleWeb;
      item.actionName.x = actionNameX;
      item.actionName.y = actionNameY;
      item.actionArrow.x = item.actionName.width / 2 - actionArrowX;
      item.actionArrow.y = actionArrowY;
    });
  }

  handleHistoryBlocksCreator(isInitial = false) {
    Object.keys(this.historyBlocks).forEach((key) => {
      const betBlockArray = this.historyBlocks[key];
      if (betBlockArray.length) {
        betBlockArray.forEach((betBlockItem) => {
          if (
            !this.historyBlocksArray.some(
              (item) =>
                item.betBlockItem.min === betBlockItem.min &&
                +item.roundId === +betBlockItem.roundId
            )
          ) {
            const currentBetBlock = new BetBlock({
              ...betBlockItem,
              isInHistory: true,
              isInitial: isInitial,
            });
            this.handleSetBlockAmountAndBg({
              isInHistory: true,
              currencyImg: Bitcoin,
              blockImg: betBlockItem.winAmount ? winBlock : disabledBlock,
              borderColor: betBlockItem.winAmount ? winBlockColor : null,
              block: currentBetBlock,
              isWin: !!betBlockItem.winAmount,
            });
            this.historyBlocksArray.push({ ...currentBetBlock, roundId: key });
            currentBetBlock.container.x = betBlockItem.x;
            currentBetBlock.container.y = betBlockItem.y;
            currentBetBlock.container.interactive = true;
            currentBetBlock.bgSprite.cursor = 'default';
            this.historyContainer.addChild(currentBetBlock.container);
          } else {
            this.historyBlocksArray.forEach((block) => {
              if (
                +this.roundInfo.roundId === +block.roundId &&
                this.roundInfo?.resultPoint >= block.betBlockItem.min &&
                this.roundInfo?.resultPoint < block.betBlockItem.max
              ) {
                this.handleSetBlockAmountAndBg({
                  isInHistory: true,
                  currencyImg: Bitcoin,
                  blockImg: winBlock,
                  borderColor: winBlockColor,
                  block,
                  isWin: true,
                });
              }
            });
          }
        });
      }
    });
  }

  handleBetBlocksCreator() {
    Object.keys(this.betBlocks).forEach((key, keyIndex) => {
      const betBlockArray = this.betBlocks[key];
      let yStep = 0;
      if (keyIndex) {
        const differentBlockMin =
          this.betBlocks[Object.keys(this.betBlocks)[keyIndex]][0].min -
          this.betBlocks[Object.keys(this.betBlocks)[0]][0].min;
        yStep = Math.round(differentBlockMin / 5);
      }
      if (betBlockArray.length) {
        betBlockArray.forEach((betBlockItem, betBlockIndex) => {
          const isBetBlockInHistory = this.historyBlocksArray.find(
            (item) => +item.roundId === +key && item.betBlockItem.min === betBlockItem.min
          );
          if (!isBetBlockInHistory) {
            const currentBetBlock = new BetBlock({
              ...betBlockItem,
              betBlockIndex,
              roundId: key,
              amount: this.selectedAmount,
            });
            const currentBetBlockIndex = this.betBlocksArray.push(currentBetBlock) - 1;
            const x = this.firstBetBlockX + this.blockWidth * keyIndex;
            const y = +mathJS.format(
              this.firstBetBlockY -
                +mathJS.format(yStep * this.blockHeight, mathConfig) +
                +mathJS.format(this.blockHeight * betBlockIndex, mathConfig) +
                pixiComponents.PixiComponent.chart.pixelDeviation,
              mathConfig
            );
            currentBetBlock.container.x = x;
            currentBetBlock.container.y = y;
            const isBetBlochShow = this.historyBlocksArray.find(
              (item) =>
                item.betBlockItem.roundId === key && item.betBlockItem.min === betBlockItem.min
            );
            if (isBetBlochShow) {
              currentBetBlock.container.visible = false;
            }
            currentBetBlock.container.interactive = true;
            currentBetBlock.container.buttonMode = true;

            currentBetBlock.container.addEventListener('pointerdown', () => {
              this.updateBetModel(
                this.handleCreatBetModel({
                  ...betBlockItem,
                  x,
                  y,
                  roundId: key,
                }),
                this.betBlocksArray[currentBetBlockIndex]
              );
            });
            this.addPointerEvent(currentBetBlockIndex);
            this.container.addChild(currentBetBlock.container);
          }
        });
      }
    });
  }

  addPointerEvent(betBlockIndex) {
    if (!isMobile() && this.betBlocksArray[betBlockIndex].roundId === 'row') {
      this.betBlocksArray[betBlockIndex].bgSprite.addEventListener('pointerover', () => {
        this.betBlocksArray[betBlockIndex].bgSprite.texture = Texture.from(selectedArrow);
      });
      this.betBlocksArray[betBlockIndex].bgSprite.addEventListener('pointerout', () => {
        if (
          this.betBlocksArray[betBlockIndex] &&
          !this.betBlocksArray[betBlockIndex].betBlockItem.isSelected
        ) {
          this.betBlocksArray[betBlockIndex].bgSprite.texture = Texture.from(defaultArrow);
        }
      });
    } else if (!isMobile()) {
      this.betBlocksArray[betBlockIndex].container.addEventListener('pointerover', () => {
        this.betBlocksArray[betBlockIndex].border.lineStyle(1, selectedBlockColor, 1);
        this.betBlocksArray[betBlockIndex].border.drawRoundedRect(
          this.betBlocksArray[betBlockIndex].bgSprite.x,
          this.betBlocksArray[betBlockIndex].bgSprite.y,
          isMobile() ? mobWidth : webWidth,
          isMobile() ? mobHeight : webHeight,
          2
        );
        this.betBlocksArray[betBlockIndex].border.visible = true;
      });
      this.betBlocksArray[betBlockIndex].container.addEventListener('pointerout', () => {
        if (
          this.betBlocksArray[betBlockIndex] &&
          !this.betBlocksArray[betBlockIndex].betBlockItem.isSelected
        ) {
          this.betBlocksArray[betBlockIndex].border.visible = false;
        }
      });
    }
  }

  handleBetActionsCreator(parent) {
    const betActions = [this.betTypes.over, this.betTypes.under];
    betActions.forEach((item) => {
      const currentActionBlock = new BetBlock({
        marketType: item,
        odd: 2,
        type: 'action',
        amount: this.selectedAmount,
      });
      this.actionBlocksArray.push(currentActionBlock);
      currentActionBlock.container.interactive = true;
      this.actionPointerDownHandler(currentActionBlock, item);
      parent.addChild(currentActionBlock.container);
    });
  }

  actionPointerDownHandler(currentActionBlock, item) {
    currentActionBlock.container.addEventListener('pointerdown', () => {
      this.updateBetModel(
        this.handleCreatBetModel(
          {
            roundId: this.roundInfo.roundId,
          },
          item
        ),
        currentActionBlock
      );
    });
  }

  handleBetValidationByType(betBlockItem) {
    const { userBalance } = userInfoStore;
    const betTotalAmount = this.betModel.reduce((acc, cur) => {
      acc += cur.amount;
      return acc;
    }, 0);

    if (betBlockItem.amount <= 0) {
      return false;
    }

    if (betTotalAmount + betBlockItem.amount > userBalance) {
      notificationStore.setNotification(
        ValidationResponseCodeForGameByRound[ResponseTypeForGamesByRound.NotEnoughBalance]
      );
      return false;
    }

    soundService.play(FastTradeSoundsName.betBlock);
    if (betBlockItem.roundId === 'row') {
      const currentRowBetBlocksCount = this.betBlocksArray.reduce((acc, cur) => {
        if (cur.betBlockItem.min === betBlockItem.min && cur.roundId !== 'row') {
          acc += 1;
        }
        return acc;
      }, 0);
      const currentRowBetsInBetModelCount = this.betModel.reduce((acc, cur) => {
        if (cur.min === betBlockItem.min) {
          acc += 1;
        }
        return acc;
      }, 0);
      return (
        betTotalAmount +
          betBlockItem.amount * (currentRowBetBlocksCount - currentRowBetsInBetModelCount) <=
        userBalance
      );
    }
    return true;
  }

  updateBetModel(betBlockItem, currentBetBlock) {
    const isValid = this.handleBetValidationByType(betBlockItem);
    const { isBetsClosed } = fastTradeBetStore;
    if (isBetsClosed) {
      return;
    }
    if (betBlockItem.marketType !== this.betTypes.range && isValid) {
      const isUndoActionIndex = this.betModel.findIndex(
        ({ marketType }) => betBlockItem.marketType === marketType
      );
      if (isUndoActionIndex >= 0) {
        this.actionBlocksArray.forEach((item) => {
          if (betBlockItem.marketType === item.betBlockItem.marketType) {
            item.betBlockItem.isSelected = false;
          }
        });
        this.betModel.splice(isUndoActionIndex, 1);
        const img = betBlockItem.marketType === this.betTypes.over ? overDefault : underDefault;
        currentBetBlock.bgSprite.texture = Texture.from(img);
        currentBetBlock.amountText.text = +mathJS.format(
          this.selectedAmount * currentBetBlock.betBlockItem.odd,
          mathConfig
        );
        return;
      }
      if (isValid) {
        this.actionBlocksArray.forEach((item) => {
          if (betBlockItem.marketType === item.betBlockItem.marketType) {
            item.betBlockItem.isSelected = true;
          }
        });
        currentBetBlock.bgSprite.texture = Texture.from(actionSelected);
        // currentBetBlock.amountText.text = betBlockItem.amount;
        this.betModel.push(betBlockItem);
      }
      return;
    }

    if (betBlockItem.roundId === 'row') {
      if (currentBetBlock.betBlockItem.isSelected) {
        if (isMobile()) {
          currentBetBlock.bgSprite.texture = Texture.from(defaultArrow);
        }
        this.betBlocksArray.forEach((item) => {
          if (item.betBlockItem.min === betBlockItem.min && item.roundId !== 'row') {
            this.handleSetBlockAmountAndBg({
              isRow: true,
              currencyImg: null,
              blockImg: defaultBlock,
              block: item,
            });
            item.betBlockItem.isSelected = false;
          }
        });
        this.betModel = this.betModel.filter(({ min }) => min !== betBlockItem.min);
        currentBetBlock.betBlockItem.isSelected = false;
        return;
      }
      if (isValid) {
        currentBetBlock.bgSprite.texture = Texture.from(selectedArrow);
        currentBetBlock.betBlockItem.isSelected = true;
        this.betBlocksArray.forEach((item) => {
          if (item.betBlockItem.min === betBlockItem.min && item.betBlockItem.roundId !== 'row') {
            this.handleSetBlockAmountAndBg({
              currencyImg: Bitcoin,
              blockImg: selectedBlock,
              block: item,
              borderColor: selectedBlockColor,
            });
            this.betModel.push(
              this.handleCreatBetModel({
                ...item.betBlockItem,
                x: item.container.x,
                y: item.container.y,
                roundId: item.betBlockItem.roundId,
              })
            );
            item.betBlockItem.isSelected = true;
            if (isMobile()) {
              item.border.visible = true;
            }
          }
        });
      }
    } else {
      const isUndoBetIndex = this.betModel.findIndex(
        ({ min, roundId }) => min === betBlockItem.min && roundId === betBlockItem.roundId
      );
      if (isUndoBetIndex >= 0) {
        currentBetBlock.betBlockItem.isSelected = false;
        this.handleSetBlockAmountAndBg({
          currencyImg: null,
          blockImg: defaultBlock,
          block: currentBetBlock,
        });
        const currentRowIndex = this.betBlocksArray.findIndex(
          (item) =>
            item.betBlockItem.roundId === 'row' && item.betBlockItem.min === betBlockItem.min
        );
        this.betBlocksArray[currentRowIndex].bgSprite.texture = Texture.from(defaultArrow);
        this.betBlocksArray[currentRowIndex].betBlockItem.isSelected = false;
        this.betModel.splice(isUndoBetIndex, 1);
        return;
      }
      if (isValid) {
        currentBetBlock.betBlockItem.isSelected = true;
        const isAllRowBetsSelected = this.betBlocksArray
          .filter(
            (item) =>
              item.betBlockItem.roundId !== 'row' && item.betBlockItem.min === betBlockItem.min
          )
          .every((item) => item.betBlockItem.isSelected);
        if (isAllRowBetsSelected) {
          const currentRowIndex = this.betBlocksArray.findIndex(
            (item) =>
              item.betBlockItem.roundId === 'row' && item.betBlockItem.min === betBlockItem.min
          );
          this.betBlocksArray[currentRowIndex].bgSprite.texture = Texture.from(selectedArrow);
          this.betBlocksArray[currentRowIndex].betBlockItem.isSelected = true;
        }
        this.handleSetBlockAmountAndBg({
          currencyImg: Bitcoin,
          block: currentBetBlock,
          blockImg: selectedBlock,
          borderColor: selectedBlockColor,
        });
        this.betModel.push(betBlockItem);
      }
    }
  }

  handleCreatBetModel(betBlockInfo, betType = this.betTypes.range) {
    return {
      ...betBlockInfo,
      marketType: betType,
      amount: this.selectedAmount,
    };
  }

  setSelectedAmount(amount) {
    this.selectedAmount = amount;
    this.betBlocksArray = this.betBlocksArray.map((item) => {
      const { betBlockItem, ...all } = item;
      if (item.amountText && !betBlockItem.isSelected) {
        item.amountText.text = +mathJS.format(amount * betBlockItem.odd, mathConfig);
      }
      return { ...all, betBlockItem: { ...betBlockItem, amount } };
    });
    this.actionBlocksArray = this.actionBlocksArray.map((item) => {
      const { betBlockItem, ...all } = item;
      if (item.amountText && !betBlockItem.isSelected) {
        item.amountText.text = +mathJS.format(amount * betBlockItem.odd, mathConfig);
      }
      return { ...all, betBlockItem: { ...betBlockItem, amount } };
    });
  }

  handleSetBlockAmountAndBg({
    block,
    blockImg,
    // currencyImg,
    isInHistory,
    borderColor,
    isRow = false,
    isWin,
  }) {
    if (isRow) {
      block.border.visible = false;
    }
    block.bgSprite.texture = Texture.from(blockImg);
    if (isWin) {
      if (!block.betBlockItem.isInitial) {
        block.winSpine.play(fastTradeAnimationNames.win);
        block.winSpine.addListener(
          'complete',
          () => {
            block.winSpine.visible = false;
          },
          block.winSpine,
          fastTradeAnimationNames.win
        );
      }
    }

    if (!isInHistory) {
      block.amountText.text = +mathJS.format(
        this.selectedAmount * block.betBlockItem.odd,
        mathConfig
      );
    }
    // block.currencyImg.texture = currencyImg ? Texture.from(currencyImg) : null;
    // block.currencyImg.x = block.amountText.x + block.amountText.width + 5;

    if (borderColor) {
      block.border.lineStyle(1, borderColor, 1);
      block.border.drawRoundedRect(
        block.bgSprite.x,
        block.bgSprite.y,
        isMobile() ? mobWidth : webWidth,
        isMobile() ? mobHeight : webHeight,
        2
      );
      block.border.visible = true;
    }
    if (!block.betBlockItem.isSelected && isMobile() && !block.betBlockItem.isInHistory) {
      block.border.visible = false;
    }
  }
}
